import moment from 'moment'
import { useSelector } from 'react-redux'
import useSWR from 'swr'

import type { EligibilityRequest, EligibleDiscount } from '@library/api-promotions'
import { eligibilityFetcher } from '@library/api-promotions'

import { getBasketDate } from 'selectors/basket'

type UseDeliveryDateDiscountArgs = {
  enabled: boolean
  boxType?: 'subscription' | 'transactional'
}

type UseDeliveryDateDiscountResult = {
  discount: EligibleDiscount | null
  // True while enabled but the delivery date hasn't hydrated yet; false once resolved or disabled.
  isLoading: boolean
}

type EligibilityKey = EligibilityRequest & { scope: 'promotions-eligibility' }

const fetchEligibility = ({ scope: _scope, ...request }: EligibilityKey) =>
  eligibilityFetcher(request)

export const useDeliveryDateDiscount = ({
  enabled,
  boxType,
}: UseDeliveryDateDiscountArgs): UseDeliveryDateDiscountResult => {
  const basketDate = useSelector(getBasketDate)

  const parsedDate = basketDate ? moment(basketDate) : null
  const deliveryDate = parsedDate?.isValid() ? parsedDate.format('YYYY-MM-DD') : ''

  const key: EligibilityKey | null =
    enabled && deliveryDate
      ? {
          scope: 'promotions-eligibility',
          delivery_date: deliveryDate,
          box_type: boxType,
        }
      : null

  const { data, isLoading: isFetchLoading } = useSWR(key, fetchEligibility, {
    shouldRetryOnError: false,
  })

  // SWR reports isLoading: false for a null key, but enabled && !deliveryDate means
  // we're still waiting on basket data, not a final "no discount" answer.
  const isLoading = enabled && !deliveryDate ? true : isFetchLoading

  return { discount: data?.data?.eligible?.[0] ?? null, isLoading }
}
